import { useContext, useEffect } from "react";
import { ga4sendEvent, moEngageUserEventTrack, getFBEventId } from "../user-analytics.service";
import { AppContext } from "../../app-context";
import { useRouter } from "next/router";
import { getVwoABTestVariationsEventData } from "../../methods/getABTestVariations";
import { useCsDashboard } from "../../hooks/useCsDashboard";

interface EventData {
    name: string;
    data?: any;
}
export enum WebsiteUserType {
    ADMIN = "Admin",
    CLIENT = "Client",
    TSO = "tso"
}

function getDevice() {
    const userAgent = window?.navigator?.userAgent;
    if (/android/i.test(userAgent)) {
        return "Mobile (Android)";
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return "Mobile (iOS)";
    }
    if (/windows phone/i.test(userAgent)) {
        return "Mobile (Windows Phone)";
    }
    return "Desktop";
}

export function useEventTrackerService() {
    const router = useRouter();
    const { globalData = {}, setGlobalData } = useContext(AppContext);
    const { utm = {} } = globalData as any;
    const { utm_source, utm_medium, utm_campaign } = router.query;
    const { isTso } = useCsDashboard();

    const utmFromQuery = {
        utm_source,
        utm_medium,
        utm_campaign
    };

    useEffect(() => {
        setGlobalData?.({
            ...globalData,
            utm: utmFromQuery
        });
    }, []);

    const {
        name,
        userId,
        companyId,
        serviceType,
        companyLocation,
        customerName,
        customerCompanyId,
        customerCompanyLocation,
        customerServiceType,
        customerPhone,
        phoneNum,
        isCsDashboardUser,
        abTestVariations,
        thirdPartyCompanyName
    } = useContext(AppContext);

    const NOT_APPLICABLE = "#N/A";

    const sendGAandMoEngageEvent = (
        eventData: EventData,
        trackGAEvent: Boolean = true,
        trackMoengageEvent: Boolean = true,
        trackFbCapiEvent: Boolean = false
    ) => {
        let data = {
            event_source: "web",
            user_id: userId ?? NOT_APPLICABLE,
            company_id: customerCompanyId ?? companyId ?? NOT_APPLICABLE,
            company_location: customerCompanyLocation ?? companyLocation ?? NOT_APPLICABLE,
            services: customerServiceType ?? serviceType ?? NOT_APPLICABLE,
            user_type: isCsDashboardUser ? "Admin" : "Client",
            third_party_company_name: thirdPartyCompanyName ?? NOT_APPLICABLE,
            web_device: getDevice(),
            ...(isTso && {
                user_type: "tso"
            }),
            ...getVwoABTestVariationsEventData(abTestVariations),
            ...utm,
            ...eventData.data
        };

        if (trackGAEvent) {
            let keysToDelete = [
                ...[
                    "is_premium",
                    "is_insurance_activated",
                    "load_type",
                    "from_location",
                    "from_location_id",
                    "from_location_city_id",
                    "from_location_state_id",
                    "to_location",
                    "to_location_id",
                    "to_location_city_id",
                    "to_location_state_id",
                    "material",
                    "quantity",
                    "quantity_type",
                    "required_vehicle_type",
                    "required_body_type",
                    "required_tyres",
                    "loading_time",
                    "recommended_price",
                    "expected_price",
                    "expected_price_type",
                    "payment_type",
                    "advance_percentage",
                    "odc",
                    "odc_length",
                    "odc_breadth",
                    "odc_height"
                ],
                ...[
                    "vehicle_number",
                    "current_location",
                    "current_location_id",
                    "current_location_city_id",
                    "current_location_state_id",
                    "routes",
                    "total_route",
                    "lorry_type",
                    "lorry_capacity",
                    "capacity_type",
                    "total_tyres",
                    "body_type",
                    "vehicle_size",
                    "vehicle_size_type"
                ],
                ...["bid_amount", "submitted_by", "bid_type", "amount", "sent_bid_amount"],
                ...[
                    "profile_user_id",
                    "profile_user_name",
                    "profile_company_id",
                    "profile_company_name",
                    "profile_location",
                    "profile_service_type",
                    "profile_connections",
                    "profile_phone_number",
                    "total_connections"
                ]
            ];
            const dataCloned = { ...data };
            keysToDelete.forEach((key) => delete dataCloned[key]);
            ga4sendEvent(eventData.name, { ...dataCloned });
        }
        if (trackMoengageEvent) {
            moEngageUserEventTrack(eventData.name, {
                user_name: NOT_APPLICABLE,
                company_name: customerName ?? name ?? NOT_APPLICABLE,
                phone_number: customerPhone ?? phoneNum ?? NOT_APPLICABLE,
                ...data
            });
        }

        if (trackFbCapiEvent) {
            const resolvedCompanyId = customerCompanyId ?? companyId;
            const eventID = getFBEventId(eventData.data?.load_id ?? eventData.name, resolvedCompanyId);
            const phone = customerPhone ?? phoneNum;

            // Keep Meta payloads small so Pixel `eid` isn't dropped on long GET requests.
            // Full `data` is for GA/MoEngage only — passing it to fbq breaks deduplication.
            const metaCustomData: Record<string, string | number> = {};
            if (eventData.data?.load_id != null) {
                metaCustomData.load_id = eventData.data.load_id;
            }
            if (eventData.data?.bid_id != null) {
                metaCustomData.bid_id = eventData.data.bid_id;
            }

            window.fbq?.("trackCustom", eventData.name, metaCustomData, { eventID });
            // void sendFbCapiEvent({
            //     event_id: eventID,
            //     event_name: eventData.name,
            //     phone_num: phone,
            //     custom_data: metaCustomData
            // });
        }
    };

    return { sendGAandMoEngageEvent };
}
